import { signal } from "@preact/signals";
import { createRef, render } from "preact";
import { useRef } from "preact/hooks";
import { type KittenSound, sounds } from "../sound.ts";
import type { Spot } from "../workshop.ts";
import { Icon, type IconName } from "./icon.tsx";
import styles from "./title.module.css";

/** The title screen's state: the game writes it, the screen follows. */
export const title = {
  /** Shown over the workshop; hidden (fading out) while a screen opened from it is up. */
  shown: signal(true),
  /** Clay left kneading, to come back to: what it is and how many strokes it has had. */
  resume: signal<{ name: string; strokes: number } | null>(null),
};

/** The menu's pane, beside which (or above which) the workshop is drawn. */
export const titlePane = createRef<HTMLElement>();

/** What the title screen asks of the workshop. */
export interface TitleActions {
  /** Moves in on a spot of the workshop and opens its screen. */
  go(spot: Spot): void;
  /** Looks at a spot while its menu item is pointed at (null: back at the whole room). */
  preview(spot: Spot | null): void;
  /** Back to the clay left kneading (see title.resume). */
  resume(): void;
}

const ITEMS: { spot: Spot; name: string; line: string }[] = [
  { spot: "letters", name: "依頼の手紙", line: "届いた依頼を読んで、引き受ける" },
  { spot: "free", name: "自由にこねる", line: "依頼なしで、好きな形に" },
  { spot: "shelf", name: "棚", line: "飾った自信作を眺める" },
  { spot: "ledger", name: "依頼帳", line: "これまでの納品の記録" },
];

/** The rest of the menu, small and side by side under it. */
const EXTRAS: { spot: Spot; name: string; icon: IconName }[] = [
  { spot: "howto", name: "遊び方", icon: "help" },
  { spot: "settings", name: "設定", icon: "settings" },
  { spot: "credits", name: "クレジット", icon: "medal" },
];

/** The title screen: the menu in a pane, with the workshop drawn beside it (see workshop.ts). */
export function mountTitle(root: HTMLElement, actions: TitleActions): void {
  render(<TitleScreen actions={actions} />, root);
}

function TitleScreen({ actions }: { actions: TitleActions }) {
  // A menu item looks at its spot while pointed at (or focused), and goes there when chosen.
  const pointing = (spot: Spot) => ({
    onClick: () => actions.go(spot),
    onPointerEnter: () => actions.preview(spot),
    onFocus: () => actions.preview(spot),
    onPointerLeave: () => actions.preview(null),
    onBlur: () => actions.preview(null),
  });
  return (
    <div class={title.shown.value ? styles.screen : `${styles.screen} ${styles.hidden}`}>
      <section class={styles.pane} ref={titlePane}>
        <h2 class={styles.heading}>
          <img class={styles.logo} src="logo.svg" alt="Kneading Needed -こねこのこねこね工房-" />
        </h2>
        <p class={styles.lead}>ねこ商会の工房へようこそ。届いた依頼の品を、粘土をこねて作ろう。</p>
        <nav class={styles.menu} aria-label="メニュー">
          {title.resume.value && (
            // The clay left kneading, on the workbench.
            <button
              type="button"
              class={`${styles.item} ${styles.resume}`}
              {...pointing("free")}
              onClick={() => actions.resume()}
            >
              <b>つづきをこねる</b>
              <small>{`${title.resume.value.name}・${title.resume.value.strokes}手`}</small>
            </button>
          )}
          {ITEMS.map(({ spot, name, line }) => (
            <button key={spot} type="button" class={styles.item} {...pointing(spot)}>
              <b>{name}</b>
              <small>{line}</small>
            </button>
          ))}
          <div class={styles.extras}>
            {EXTRAS.map(({ spot, name, icon }) => (
              <button key={spot} type="button" class={styles.extra} {...pointing(spot)}>
                <Icon name={icon} />
                {name}
              </button>
            ))}
          </div>
        </nav>
        <Kitten />
      </section>
    </div>
  );
}

/** What the kitten says when poked, each with how it sounds. */
const LINES: [string, KittenSound][] = [
  ["にゃっ", "mew"],
  ["むにっ", "squish"],
  ["こねて〜", "meow"],
  ["にゃーん", "meow"],
  ["ふみふみ…", "knead"],
  ["ごろごろ", "purr"],
  ["にゃ？", "ask"],
];

/** Restarts a CSS animation driven by `className` on `el`. */
function replay(el: HTMLElement, className: string): void {
  el.classList.remove(className);
  void el.offsetWidth;
  el.classList.add(className);
}

/**
 * A kitten kneading dough that says something when poked. It never re-renders: the poke
 * restarts its animations on the elements directly.
 */
function Kitten() {
  const kitten = useRef<HTMLButtonElement>(null), bubble = useRef<HTMLSpanElement>(null);
  const last = useRef(-1);
  const poke = () => {
    let i: number;
    do i = Math.floor(Math.random() * LINES.length); while (i === last.current);
    last.current = i;
    const [line, sound] = LINES[i];
    bubble.current!.textContent = line;
    sounds.kitten(sound);
    replay(kitten.current!, styles.poked);
    replay(bubble.current!, styles.show);
  };
  return (
    <button
      type="button"
      class={styles.kitten}
      ref={kitten}
      aria-label="こねこをつつく"
      onClick={poke}
    >
      <span class={styles.bubble} ref={bubble} aria-live="polite" />
      <svg viewBox="0 0 200 192" aria-hidden="true">
        <defs>
          <filter id="kitten-shadow">
            <feGaussianBlur stdDeviation="1.2" />
          </filter>
        </defs>
        <g class={styles.body}>
          <path
            class={styles.tail}
            d="M150 156 q40 -4 32 -44 q-3 -12 -12 -8"
            fill="none"
            stroke="#e9a877"
            stroke-width="12"
            stroke-linecap="round"
          />
          <ellipse cx="100" cy="142" rx="62" ry="40" fill="#f2b48c" />
          {/* The forelegs, shown by their shadows on the body, move with their paws. */}
          <g class={styles.paw}>
            <rect
              x="66"
              y="112"
              width="28"
              height="60"
              rx="14"
              fill="#8a4a2c"
              opacity=".2"
              filter="url(#kitten-shadow)"
            />
            <rect x="69" y="112" width="22" height="60" rx="11" fill="#f2b48c" />
          </g>
          <g class={`${styles.paw} ${styles.pawRight}`}>
            <rect
              x="106"
              y="112"
              width="28"
              height="60"
              rx="14"
              fill="#8a4a2c"
              opacity=".2"
              filter="url(#kitten-shadow)"
            />
            <rect x="109" y="112" width="22" height="60" rx="11" fill="#f2b48c" />
          </g>
          {/* The chest, over the forelegs' tops: they draw back under it as their paws lift. */}
          <ellipse cx="100" cy="120" rx="40" ry="26" fill="#f2b48c" />
          {/* The head, raised clear of the chest. */}
          <g transform="translate(0 -12)">
            <g class={styles.earLeft}>
              <path d="M56 72 L50 24 L92 50 Z" fill="#f2b48c" />
              <path d="M61 63 L57 37 L80 52 Z" fill="#f7c4bb" />
            </g>
            <g class={styles.earRight}>
              <path d="M144 72 L150 24 L108 50 Z" fill="#f2b48c" />
              <path d="M139 63 L143 37 L120 52 Z" fill="#f7c4bb" />
            </g>
            <circle cx="100" cy="94" r="52" fill="#f2b48c" />
            <path
              d="M91 46 v10 M100 44 v12 M109 46 v10"
              stroke="#e39a6a"
              stroke-width="4"
              stroke-linecap="round"
            />
            <g class={styles.eyesOpen}>
              <ellipse cx="80" cy="92" rx="5.5" ry="7" fill="#4a3530" />
              <ellipse cx="120" cy="92" rx="5.5" ry="7" fill="#4a3530" />
              <circle cx="82" cy="89" r="1.8" fill="#fff" />
              <circle cx="122" cy="89" r="1.8" fill="#fff" />
            </g>
            <g
              class={styles.eyesSquint}
              stroke="#4a3530"
              stroke-width="3.5"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M74 86 l9 6 l-9 6" />
              <path d="M126 86 l-9 6 l9 6" />
            </g>
            <ellipse cx="66" cy="108" rx="9" ry="5" fill="#f28b82" opacity=".45" />
            <ellipse cx="134" cy="108" rx="9" ry="5" fill="#f28b82" opacity=".45" />
            <path
              d="M94 106 q3 4 6 0 q3 4 6 0"
              stroke="#4a3530"
              stroke-width="2.5"
              fill="none"
              stroke-linecap="round"
            />
            <g stroke="#b98062" stroke-width="2" stroke-linecap="round">
              <path d="M58 102 h-20 M58 108 l-18 4" />
              <path d="M142 102 h20 M142 108 l18 4" />
            </g>
          </g>
        </g>
        <ellipse
          class={styles.dough}
          cx="100"
          cy="178"
          rx="34"
          ry="11"
          fill="#fdf1dc"
          stroke="#ecd3ae"
          stroke-width="2"
        />
        <ellipse class={styles.paw} cx="80" cy="172" rx="13" ry="9" fill="#f7c8a4" />
        <ellipse
          class={`${styles.paw} ${styles.pawRight}`}
          cx="120"
          cy="172"
          rx="13"
          ry="9"
          fill="#f7c8a4"
        />
      </svg>
    </button>
  );
}
